import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Truck, AlertTriangle, Wallet, FileStack } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { API, fmt, d, STATUS, DOC_TYPES } from "@/lib/purchases";

const StatusBadge = ({ s }) => <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-px ${STATUS[s]}`}>{(s || "").replace("_", " ")}</span>;

const KPI = ({ label, value, tone = "text-slate-100", testid }) => (
  <div className="rounded-xl border border-slate-800 bg-[#151D2E] px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`font-mono-num text-lg font-bold ${tone}`}>{value}</div>
  </div>
);

function DueSoon({ items, overdue }) {
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-2" data-testid="supplier-due-soon">
      <div className="flex items-center gap-2 font-bold text-amber-200"><AlertTriangle className="w-4 h-4" /> {overdue > 0 ? `${fmt(overdue)} overdue · ` : ""}{items.length} bill{items.length === 1 ? "" : "s"} due within 7 days</div>
      <div className="flex flex-wrap gap-2 text-xs">
        {items.map((b) => <Link key={b.id} to={`/purchases/bills/${b.id}`} data-testid={`due-soon-${b.id}`} className="rounded-lg border border-amber-500/40 bg-slate-950/40 px-2.5 py-1.5 hover:bg-slate-900 font-mono-num">{b.invoice_number || b.bill_number || "bill"} · due {d(b.due_date)} · <b>{fmt(b.outstanding_amount)}</b></Link>)}
      </div>
    </div>
  );
}

function BillsTable({ bills }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-[#151D2E] overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-slate-800 text-[11px] uppercase tracking-wider font-bold text-slate-400">Bills</div>
      <table className="w-full text-sm" data-testid="supplier-bills">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th className="px-4 py-2">Date</th><th className="px-3 py-2">Bill</th><th className="px-3 py-2">Category</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Owing</th><th className="px-3 py-2">Due</th><th className="px-3 py-2">Status</th></tr></thead>
        <tbody>{bills.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No bills from this supplier yet</td></tr> : bills.map((b) => (
          <tr key={b.id} className="border-t border-slate-800 hover:bg-slate-900/40" data-testid={`supplier-bill-${b.id}`}>
            <td className="px-4 py-2 font-mono-num whitespace-nowrap">{d(b.bill_date)}</td>
            <td className="px-3 py-2"><Link to={`/purchases/bills/${b.id}`} className="text-sky-300 hover:underline font-mono-num">{b.invoice_number || b.bill_number || "—"}</Link><div className="text-xs text-slate-500">{DOC_TYPES[b.document_type]}</div></td>
            <td className="px-3 py-2 text-xs text-slate-400">{b.category_name}</td>
            <td className="px-3 py-2 text-right font-mono-num">{fmt(b.total_amount)}</td>
            <td className="px-3 py-2 text-right font-mono-num text-emerald-300">{fmt(b.amount_paid)}</td>
            <td className="px-3 py-2 text-right font-mono-num font-bold">{fmt(b.outstanding_amount)}</td>
            <td className="px-3 py-2 font-mono-num text-xs">{d(b.due_date)}</td>
            <td className="px-3 py-2"><StatusBadge s={b.status} /></td>
          </tr>))}</tbody>
      </table>
    </section>
  );
}

function PaymentsTable({ payments }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-[#151D2E] overflow-x-auto">
      <div className="px-4 py-2.5 border-b border-slate-800 text-[11px] uppercase tracking-wider font-bold text-slate-400">Payments</div>
      <table className="w-full text-sm" data-testid="supplier-payments">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th className="px-4 py-2">Date</th><th className="px-3 py-2">Bill</th><th className="px-3 py-2">Method</th><th className="px-3 py-2">Reference</th><th className="px-3 py-2 text-right">Amount</th></tr></thead>
        <tbody>{payments.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No payments recorded</td></tr> : payments.map((p) => (
          <tr key={p.id} className="border-t border-slate-800"><td className="px-4 py-2 font-mono-num whitespace-nowrap">{d(p.payment_date)}</td><td className="px-3 py-2"><Link to={`/purchases/bills/${p.bill_id}`} className="text-sky-300 hover:underline font-mono-num">{p.bill_no || "—"}</Link></td><td className="px-3 py-2 text-xs capitalize">{(p.method || "").replace("_", " ")}</td><td className="px-3 py-2 text-xs text-slate-400">{p.reference || ""}</td><td className="px-3 py-2 text-right font-mono-num text-emerald-300">{fmt(p.amount)}</td></tr>))}</tbody>
      </table>
    </section>
  );
}

export default function SupplierStatement() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/purchases/suppliers/${id}/statement`).then(({ data }) => setData(data)).catch(() => {}); }, [id]);
  const s = data?.supplier, t = data?.totals;
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-24 md:pb-8">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5" data-testid="supplier-statement">
        <Link to="/purchases/suppliers" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"><ArrowLeft className="w-4 h-4" /> Suppliers</Link>
        {!data ? <div className="text-slate-500 py-16 text-center">Loading supplier…</div> : (
          <>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold inline-flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Supplier statement</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1" data-testid="supplier-name">{s.supplier_name}</h1>
                <p className="text-sm text-slate-400 mt-1">{[s.company_name, s.uen && `UEN ${s.uen}`, s.contact_person, s.phone, s.email, s.payment_terms && `${s.payment_terms}-day terms`].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <KPI label="Billed" value={fmt(t.billed)} testid="sup-kpi-billed" />
                <KPI label="Paid" value={fmt(t.paid)} tone="text-emerald-300" testid="sup-kpi-paid" />
                <KPI label="Outstanding" value={fmt(t.outstanding)} tone="text-amber-300" testid="sup-kpi-outstanding" />
                <KPI label="Overdue" value={fmt(t.overdue)} tone={t.overdue > 0 ? "text-rose-300" : "text-slate-300"} testid="sup-kpi-overdue" />
                <KPI label="Due in 7 days" value={fmt(t.due_soon)} tone={t.due_soon > 0 ? "text-amber-200" : "text-slate-300"} testid="sup-kpi-due-soon" />
              </div>
            </div>
            <DueSoon items={data.due_soon} overdue={t.overdue} />
            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="supplier-soa-links">
              <Link to={`/purchases/statements?supplier=${id}`} className="inline-flex items-center gap-1.5 text-sky-300 hover:underline"><FileStack className="w-4 h-4" /> Supplier's statements of account</Link>
              <span className="text-slate-600">·</span>
              <Link to="/purchases/statements/new" className="text-emerald-300 hover:underline">Upload a new SOA to reconcile</Link>
              <span className="text-xs text-slate-500">(statements are reconciled, never counted as bills)</span>
            </div>
            <BillsTable bills={data.bills} />
            <PaymentsTable payments={data.payments} />
            {t.outstanding > 0 && <div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Open a bill above to record a payment.</div>}
          </>
        )}
      </main>
    </div>
  );
}
